import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSocket } from "../context/SocketContext";
import Guildhall from "../Guildhall";
import { setLocalStreamPromise, setSocketForPeers, updatePeers } from "../peers";
import { setRemoteTrackCallback, setSocketForSignaling } from "../signaling";
import { setBandwidthProfile } from "../peers";

function ChatRoomPage() {
  const { roomName } = useParams();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [hasConnected, setHasConnected] = useState(false);
  const [isReadyToConnect, setIsReadyToConnect] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [localStream, setLocalStream] = useState(null);
  const localVideoRef = useRef(null);
  const remoteVideosRef = useRef(new Map());
  const token = localStorage.getItem("token");
  const [username] = useState(() => localStorage.getItem("username"));
  const [roomLeader, setRoomLeader] = useState(null);
  const [socketIdToUsername, setSocketIdToUsername] = useState({});
  const [socketIdToDisplayName, setSocketIdToDisplayName] = useState({});
  const [userCount, setUserCount] = useState(1);
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isLowBandwidth, setIsLowBandwidth] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [cameraOffByPeer, setCameraOffByPeer] = useState({});

  useEffect(() => {
    const streamPromise = navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        console.log("📹 Local media stream acquired:", stream);
        stream.getTracks().forEach((track) =>
          console.log(`🔊 Track kind: ${track.kind}, enabled: ${track.enabled}`)
        );

        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }

        setLocalStream(stream);
        const [videoTrack] = stream.getVideoTracks();
        const off = videoTrack ? !videoTrack.enabled : true;
        setIsCameraOff(off);
        return stream;
      });

    setLocalStreamPromise(streamPromise);
    setIsReadyToConnect(true);

    setRemoteTrackCallback((peerId, track, streams) => {
      setRemoteStreams((prev) => ({
        ...prev,
        [peerId]: streams[0],
      }));
    });
  }, []);

  

function toggleCamera() {
  if (!localStream) return;

  const [videoTrack] = localStream.getVideoTracks();
  if (!videoTrack) return;

  setIsCameraOff(prevOff => {
    const nextOff = !prevOff;
    videoTrack.enabled = !nextOff;
    console.log(`📷 Camera ${nextOff ? "OFF" : "ON"} (track.enabled=${videoTrack.enabled})`);
    socket?.emit("camera-state", { cameraOff: nextOff });
    return nextOff;
  });
}


  useEffect(() => {
    console.log("🧠 Current username state:", username);

    if (!token || !username || !roomName) {
      console.warn("⚠️ Missing token/username/roomName. Redirecting...");
      navigate("/");
    }
  }, [token, username, roomName, navigate]);

  useEffect(() => {
  if (!socket) return;

  const handleClientList = ({ clients, leader, cameraOffByPeer }) => {
    if (!Array.isArray(clients)) {
      console.warn("⚠️ Malformed client list payload:", clients);
      return;
    }

    console.log("📜 Received client list:", clients);
    setRoomLeader(leader);
    setUserCount(clients.length);

    const usernameMap = {};
    const displayNameMap = {};

    clients.forEach(({ socketId, username, displayName }) => {
      usernameMap[socketId] = username;
      displayNameMap[socketId] = displayName || username;
    });

    updatePeers(clients);
    setSocketIdToUsername(usernameMap);
    setSocketIdToDisplayName(displayNameMap);

    // ✅ NEW: snapshot of who has camera off
    if (cameraOffByPeer && typeof cameraOffByPeer === "object") {
      setCameraOffByPeer(cameraOffByPeer);
    }
  };

  socket.on("client_list", handleClientList);

  return () => {
    socket.off("client_list", handleClientList);
  };
}, [socket]);

useEffect(() => {
  if (!socket) return;

  const handleCameraState = ({ peerId, cameraOff }) => {
    if (!peerId) return;
    setCameraOffByPeer(prev => ({ ...prev, [peerId]: !!cameraOff }));
  };

  socket.on("camera-state", handleCameraState);
  return () => socket.off("camera-state", handleCameraState);
}, [socket]);


  useEffect(() => {
    if (!socket || !username || !roomName || !token) {
      console.warn("⚠️ Cannot emit join-room — missing socket/token/username/roomName");
      return;
    }

    setSocketForPeers(socket);
    setSocketForSignaling(socket);

    socket.on("peer-disconnected", (peerId) => {
      console.log(`👻 Peer ${peerId} disconnected. Cleaning up...`);
      setRemoteStreams((prev) => {
        const updated = { ...prev };
        delete updated[peerId];
        return updated;
      });
    });
  }, [socket, token, username, roomName]);

  useEffect(() => {
  if (!socket) return;

  const handleForceDisconnect = ({ reason }) => {
    alert(reason || "You were disconnected.");
    
    // Stop local stream
  if (localStream) {
    localStream.getTracks().forEach(track => track.stop());
  }
  
    // Optionally stop streams, clear tokens, etc.
    localStorage.removeItem("token");
    
    // Redirect to landing page or login
    navigate("/");
  };

  socket.on("force-disconnect", handleForceDisconnect);

  return () => {
    socket.off("force-disconnect", handleForceDisconnect);
  };
}, [socket, navigate]);

useEffect(() => {
  if (!socket) return;

  const handleDisplayNameUpdate = ({ id, displayName }) => {
    console.log(`🎯 Received display_name_update:`, { id, displayName });

    setSocketIdToDisplayName(prev => ({
      ...prev,
      [id]: displayName
    }));
  };

  socket.on("display_name_update", handleDisplayNameUpdate);

  return () => {
    socket.off("display_name_update", handleDisplayNameUpdate);
  };
}, [socket]);

useEffect(() => {
  if (socket && roomName) {
    socket.emit("guild-summoned-status-request");
  }
}, [socket, roomName]);

useEffect(() => {
  if (!socket) return;

  const handleRoomStateUpdate = ({ room, count }) => {
  setUserCount(count);
};

  socket.on("room-state-update", handleRoomStateUpdate);
  return () => {
    socket.off("room-state-update", handleRoomStateUpdate);
  };
}, [socket]);

useEffect(() => {
  if (!socket) return;

  const handleGuildSummoned = () => {
    setIsSessionActive(true);
  };

  socket.on("guild-summoned", handleGuildSummoned);
  return () => {
    socket.off("guild-summoned", handleGuildSummoned);
  };
}, [socket]);

  const toggleMute = () => {
    if (localStream) {
      localStream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
        setIsMuted(!track.enabled);
      });
    }
  };
  
  const toggleLowBandwidth = () => {
  const newValue = !isLowBandwidth;
  setIsLowBandwidth(newValue);

  // Tell peers.js to apply the new profile
  setBandwidthProfile(newValue ? "low" : "normal");
};

  const handleConnectClick = () => {
    const { startSignaling } = require("../signaling");
    startSignaling();
    setHasConnected(true);
    socket.emit("connect-to-guild");
  };

  const handleLogout = async () => {
    localStorage.clear();
    navigate("/");
  };
  return (
    <Guildhall
      localStream={localStream}
      localVideoRef={localVideoRef}
      remoteStreams={remoteStreams}
      setRemoteStreams={setRemoteStreams}
      remoteVideosRef={remoteVideosRef}
      isMuted={isMuted}
      toggleMute={toggleMute}
      isCameraOff={isCameraOff}
      toggleCamera={toggleCamera}
      cameraOffByPeer={cameraOffByPeer}
      isLowBandwidth={isLowBandwidth}
      toggleLowBandwidth={toggleLowBandwidth}
      hasConnected={hasConnected}
      isReadyToConnect={isReadyToConnect}
      handleConnectClick={handleConnectClick}
      username={username}
      socket={socket}
      setHasConnected={setHasConnected}
      setIsReadyToConnect={setIsReadyToConnect}
      handleLogout={handleLogout}
      roomLeader={roomLeader}
      socketIdToUsername={socketIdToUsername}
      roomName={roomName}
      userCount={userCount}
      socketIdToDisplayName={socketIdToDisplayName}
      isSessionActive={isSessionActive}
    />
  );
}

export default ChatRoomPage;
